import type {
  AnyObject,
  APIResponse,
  Maybe,
  Nullable,
  RequestedTimeType,
  ScheduleType,
  TimeType,
  TranslatableLabel,
} from '@/types';
import type { Ref } from 'vue';
import type { Problem } from '@/api/sirta/problems';
import type { AsyncStateReturn } from '@/components/common/AsyncLoader/asyncLoader';
import type { UseAppFetchReturn } from '@/api/utils/useAppFetch.ts';
import type { ImmutableReason } from '@/types/serviceRequest/serviceRequestDetails.types.ts';

export type TripTime = {
  lowerBound: string | null;
  upperBound: string | null;
  requestedTime: string | null;
  requestedTimeType: RequestedTimeType;
  /** Le déplacement ("leg") est confirmé ou non */
  isConfirmed: boolean;
};

export type TripType = 'oneWay' | 'roundTrip' | 'multiDestination';

export type CustomerTripStatus = 'processing' | 'confirmed' | 'hasInterruptions';

export interface CustomerTrip {
  scheduleType: ScheduleType;
  tripKey: string;
  tripIndex: number | null;
  date: string;
  type: TripType;
  trips: TripsData[];
  status: CustomerTripStatus;
}

export interface CustomerTrips {
  /** Date opérationnelle */
  currentOperationalDate: string | null;
  /** Liste des dates à afficher à l'utilisateur, afin de permettre le filtrage de la liste de déplacements */
  possibleDates: string[];
  /** Liste des déplacements */
  trips: CustomerTrip[];
  /**
   * Nombre de déplacements additionnels pour la journée, lorsqu'un nombre maximal de déplacements à retourné est
   * demandé durant la requête backend
   */
  filteredCount: number;
}

export interface ServiceRequestsTemplateOverview {
  /** Liste des déplacements */
  trips: CustomerTrip[];
}

export interface TripsData {
  /**
   * Un déplacement "multi-destination" contient une liste de déplacement "aller-simple" ("oneWay") alors qu'un
   * aller-retour conserve le type "roundTrip"
   */
  tripType: Extract<TripType, 'oneWay' | 'roundTrip'>;
  locations: TripLocation[];
}

export interface TripLocationAddress {
  type: TripLocationAddressType;
  title: TranslatableLabel;
  text?: TranslatableLabel[];
}

export type TripLocationAddressType = 'home' | 'landmark' | 'other';

export interface TripLocation {
  address: TripLocationAddress;
  time: TripTime;
  type: TimeType;
  note: TranslatableLabel | null;
  coordinateWgs84: [number, number];
}

export interface TripAssistiveDevices {
  totalCount: number;
  labels: TranslatableLabel;
}

export type TripPayment =
  | { type: 'free' }
  | { type: 'cash'; fare: number }
  | { type: 'other'; description: string };

export interface ITripDetailsLeg {
  /** id unique du segment actuel */
  legKey: string;
  /** Nombre total de segments de la liste où est situé le segment actuel */
  totalLegs: number;
  type: 'inwardTrip' | 'outwardTrip' | 'multiTrip'; // "multiTrip" est utilisé en conjonction avec "tripNumber" ci-dessous
  locations: TripLocation[];
  assistiveDevices: TripAssistiveDevices;
  /** Nombre d'accompagnateurs dans le segment actuel */
  numberOfCompanions: number;
  payment?: TripPayment;
  nonModifiableReason?: ImmutableReason;
  /** retourne le numéro du déplacement défini dans la journée, dans le cas d'un déplacement multi-destination. */
  tripNumber: Maybe<number>;
}

export interface TripDetailsDatedProps extends Omit<CustomerTrip, 'trips' | 'scheduleType'> {
  otherTrips: CustomerTrip[];
  trips: ITripDetailsLeg[];
  nonModifiableReason: ImmutableReason | null;
  nonExtendableReason: ImmutableReason | null;
  nonDuplicableReason: ImmutableReason | null;
}

export class TripDetailsDated implements Omit<CustomerTrip, 'trips'> {
  readonly scheduleType = 'dated' as const;

  readonly date: string;
  readonly status: CustomerTripStatus;
  readonly tripKey: string;
  readonly tripIndex: number | null;
  readonly type: TripType;
  readonly otherTrips: CustomerTrip[];
  readonly trips: ITripDetailsLeg[];
  readonly nonModifiableReason: ImmutableReason | null;
  readonly nonExtendableReason: ImmutableReason | null;
  readonly nonDuplicableReason: ImmutableReason | null;

  constructor(props: TripDetailsDatedProps) {
    this.date = props.date;
    this.status = props.status;
    this.tripKey = props.tripKey;
    this.tripIndex = props.tripIndex;
    this.type = props.type;
    this.otherTrips = props.otherTrips;
    this.trips = props.trips;
    this.nonModifiableReason = props.nonModifiableReason;
    this.nonExtendableReason = props.nonExtendableReason;
    this.nonDuplicableReason = props.nonDuplicableReason;
  }

  get isModifiable() {
    return this.nonModifiableReason === null;
  }

  get isExtendable() {
    return this.nonExtendableReason === null;
  }

  get isDuplicable() {
    return this.nonDuplicableReason === null;
  }

  get hasModifiableLegs() {
    return this.trips.some((trip) => !trip.nonModifiableReason);
  }
}

/** propriétés affichées dans le haut de la page Trip Details, que ce soit pour un type "dated" ou "template" */
export type TripDetailsHeader = Pick<CustomerTrip, 'type' | 'tripIndex' | 'tripKey' | 'date' | 'status' | 'scheduleType'> & {
  legKeys: string[];
  servicePeriod?: Nullable<TripDetailsServicePeriod>;
  interruptions: ServiceRequestInterruption[];
};

/** Date d'activation et de désactivation d'une demande de service régulière */
export interface TripDetailsServicePeriod {
  startDate: string;
  endDate: string;
}

export interface ServiceRequestInterruption {
  startDate: string;
  endDate: string;
}

/** Détails d'une demande de service de type régulière */
export interface TripDetailsTemplate extends TripDetailsServicePeriod, Omit<CustomerTrip, 'trips'> {
  scheduleType: 'template';
  isCancellable: boolean;
  isInterruptable: boolean;
  otherTrips: CustomerTrip[];
  trips: ITripDetailsLeg[];
  interruptions: ServiceRequestInterruption[];
}

export type TripDetailsApiComposerReturn<Resp extends AnyObject, Err extends Problem = Problem> = {
  fetchData: (key: Ref<string>) => AsyncStateReturn<Resp, Err, false>;
  apiResponse: APIResponse<Resp, Err>;
  /** fonction d'annulation de la requête en cours */
  abort: UseAppFetchReturn<never>['abort'];
};
